import React, { ReactElement, forwardRef, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Action } from "redux";
import { Avatar } from "@chakra-ui/react";
import { codeBenchActions, gradeBenchPanelActions, commonEnums, Utils } from "@codametrix/ui-common";
import { CMXIcon } from '@codametrix/ui-components';
import { Textarea } from "../textarea/textarea";

export type UserActivityCommentProps = {
  onFilterChange?: (filter: "comments" | "all") => void;
  editable: boolean;
};

export const UserActivityComment = forwardRef<HTMLTextAreaElement, UserActivityCommentProps>(
  ({ onFilterChange, editable }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement & { resetTextLength?: () => void }>(null);

    const dispatch = useDispatch();
    const codeBenchState = useSelector((state: CMx.ApplicationState) => state.codeBench);
    const gradeBenchState = useSelector((state: CMx.ApplicationState) => state.gradeBenchPanel);
    const uiState = useSelector((state: CMx.ApplicationState) => state.ui);
    
    const isGradeBench = codeBenchState?.benchMode === commonEnums.BenchMode.GRADE;
    const processInstance = codeBenchState?.processInstance;
    const activeUser = uiState?.activeUser;
    const sdCaseId = gradeBenchState?.invoices?.[0]?.sdCaseId;

    const userAvatar: ReactElement | null = React.useMemo(() => {
      if (!activeUser) return null;
      const initials = Utils.getAvatarInitials(
        activeUser.firstName,
        activeUser.lastName
      );
      return (
        <Avatar
          name={initials}
          bgColor="#6B8D99"
          color="#FFFFFF"
          size="sm"
        />
      );
    }, [activeUser]);

    const [selectedFilter, setSelectedFilter] =
      React.useState<"comments" | "all">("all");
    const [hasText, setHasText] = React.useState(false);


    const clearTextarea = () => {
      if (!textareaRef.current) return;

      textareaRef.current.value = "";
      try {
        textareaRef.current.dispatchEvent(new Event("input", { bubbles: true }));
      } catch (e) {
        console.warn("Input event dispatch failed:", e);
      }
      textareaRef.current.resetTextLength?.();
      setHasText(false);
    };

    const handleSaveClick = () => {
      const comment = textareaRef.current?.value.trim();
      if (!comment) return;

      if (isGradeBench) {
        // Use grade bench action
        dispatch(
          gradeBenchPanelActions.addUserActivityComment({
            sdCaseId,
            comment,
          }) as any as Action
        );
      } else {
        // Use code bench action
        dispatch(
          codeBenchActions.addUserActivityComment({
            processInstance,
            comment,
          }) as any as Action
        );
      }

      clearTextarea();
    };

    const handleCancelClick = () => {
      clearTextarea();
    };

    const handleFilterClick = (filter: "comments" | "all") => {
      setSelectedFilter(filter);
      onFilterChange?.(filter);
    };
   
    return (
      <div className="comment-send">
        <Textarea
          ref={textareaRef}
          leftIcon={userAvatar}
          placeholder="Add a comment"
          onChange={(e) => setHasText(!!e.target.value.trim())}
          readonly={!editable}
        />

        <div className="comment-actions">
          <button
            className={`chakra-secondary-button ${!hasText ? "disabled" : ""}`}
            onClick={handleCancelClick}
          >
            Cancel
          </button>
          <button
            className={`chakra-primary-button ${!hasText ? "disabled" : ""}`}
            onClick={handleSaveClick}
          >
            Save
          </button>
        </div>

        <div className="comment-filter">
          <button
            onClick={() => handleFilterClick("comments")}
            className={`chakra-secondary-button ${
              selectedFilter === "comments" ? "active" : ""
            }`}
          >
            {selectedFilter === "comments" && (
               <CMXIcon
                      svgIconName="BiomeCheckSVG20"
                      svgIconSize="20"
                      svgIconClass="left-arrow"
                    />
            )}
            Comments
          </button>

          <button
            onClick={() => handleFilterClick("all")}
            className={`chakra-secondary-button ${
              selectedFilter === "all" ? "active" : ""
            }`}
          >
            {selectedFilter === "all" && (
              <CMXIcon
                      svgIconName="BiomeCheckSVG20"
                      svgIconSize="20"
                    />
            )}
            All
          </button>
        </div>
      </div>
    );
  }
);

UserActivityComment.displayName = "UserActivityComment";
